import { useEffect, useState } from "react";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import Login from "@/pages/Login";
import { SLUG_TO_ROLE, basePathForRole } from "@/lib/roleRoutes";
import TileSphereLogo from "@/components/TileSphereLogo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Tenant-scoped login: /<tenant-slug>/<role-slug>/login
export default function TenantRoleLogin() {
  const { tenantSlug, roleSlug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const expectedRole = SLUG_TO_ROLE[roleSlug];

  useEffect(() => {
    if (!expectedRole) {
      setError(`Unknown role "${roleSlug}".`);
      setLoading(false);
      return;
    }
    axios
      .get(`${BACKEND}/api/public/tenants/${tenantSlug}`)
      .then(({ data }) => setTenant(data))
      .catch((e) => setError(e.response?.data?.detail || "Tenant not found."))
      .finally(() => setLoading(false));
  }, [tenantSlug, roleSlug, expectedRole]);

  // If already logged in, validate + redirect
  useEffect(() => {
    if (!user || !expectedRole) return;
    if (user.role === expectedRole) {
      navigate(`/${tenantSlug}/${roleSlug}`, { replace: true });
    }
  }, [user, expectedRole, tenantSlug, roleSlug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a14] text-white flex items-center justify-center" data-testid="tenant-login-loading">
        <div className="text-white/50">Loading portal…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a14] text-white flex flex-col items-center justify-center p-6 gap-4" data-testid="tenant-login-error">
        <TileSphereLogo />
        <div className="text-2xl font-bold">Portal not found</div>
        <div className="text-white/60 text-sm">{error}</div>
        <a href="/" className="text-orange-400 underline mt-2">Back to home</a>
      </div>
    );
  }

  const accentLabel = (
    <>to the <span className="font-semibold text-white">{tenant?.branding?.company_name || tenant?.name}</span> portal · <span className="text-orange-300 font-mono">{roleSlug}</span></>
  );

  return <Login hideDemo hideRegister accentLabel={accentLabel} enforceSlug={tenantSlug} enforceRole={expectedRole} />;
}
